import {
    useReviewsPageDataContext,
    useReviewsPageQueryContext,
    useReviewsPageSelectionContext,
    useReviewsPageTransportContext,
} from '@/context/reviews-page.context';
import { useAuth } from '@/context/auth.context';
import { useReviewAnswerJobs } from '@/context/review-answer-jobs.context';
import { useFeedbacksTableSelection } from '@/components/feedbacks-table/use-feedbacks-table-selection';
import { useFeedbacksTableFilters } from '@/components/feedbacks-table/use-feedbacks-table-filters';
import { useFeedbacksTableModals } from '@/components/feedbacks-table/use-feedbacks-table-modals';
import { createFeedbacksTableColumns } from '@/components/feedbacks-table/feedbacks-table-columns';
import { buildFeedbacksTableControllerBindings } from '@/components/feedbacks-table/feedbacks-table-controller-bindings.builder';
import { useSelectionReviewsAction } from '@/components/action-buttons/use-selection-reviews-action';
import { usePickedReviewsRef } from '@/components/action-buttons/use-picked-reviews-ref';
import { TableHeaderSelectAllButton } from '@/components/table-selection/TableSelectionHeaderControls';
import { createElement, useState } from 'react';
import type {
    FeedbacksTableControllerParams,
    FeedbacksTableControllerTableBindings,
    FeedbacksTableControllerValue,
} from '@/components/feedbacks-table/feedbacks-table-controller.types';

export const useFeedbacksTableController = ({
    onFiltersChange,
    defaultFilterValues,
}: FeedbacksTableControllerParams): FeedbacksTableControllerValue => {
    const { selectionSessionId, pickedReviews, setPickedReviews } = useReviewsPageSelectionContext();
    const { reviews, products, brandGroups, total } = useReviewsPageDataContext();
    const { sort, filtersQuery, maxSelectedItems } = useReviewsPageQueryContext();
    const {
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
        isReviewsDataReconciliationSelectionVisualRunning = false,
    } = useReviewsPageTransportContext();
    const { accessToken } = useAuth();
    const { getAnswerJob } = useReviewAnswerJobs();
    const [isAllPickLoading, setIsAllPickLoading] = useState(false);
    const { getCurrentPickedReviews } = usePickedReviewsRef(pickedReviews);
    const filters = useFeedbacksTableFilters({
        defaultFilterValues,
        onFiltersChange,
        products,
        brandGroups,
    });
    const { onRowClick, onPhotoPreviewOpen, onAiResponseOpen, ...modalsBindings } = useFeedbacksTableModals({
        accessToken,
    });
    const { onHeaderCheckboxChange, onCheckboxChange, isHeaderChecked, checkboxData } = useFeedbacksTableSelection({
        accessToken,
        selectionSessionId,
        pickedReviews,
        setPickedReviews,
        reviews,
        maxSelectedItems,
        enqueueReviewSelectionMutation,
        waitForReviewSelectionReconcile,
    });
    const { onAllPickedReviews } = useSelectionReviewsAction({
        accessToken,
        selectionSessionId,
        total,
        filtersQuery,
        sort,
        maxSelectedItems,
        setPickedReviews,
        waitForReviewSelectionReconcile,
        enqueueReviewSelectionMutation,
        getCurrentPickedReviews,
        setIsAllPickLoading,
    });
    const { columns, headerSecondaryRow } = createFeedbacksTableColumns({
        filters,
        pickedReviews,
        isHeaderChecked,
        isSelectionSyncing: isReviewsDataReconciliationSelectionVisualRunning,
        onHeaderCheckboxChange,
        getAnswerJob,
        onPhotoPreviewOpen,
        onAiResponseOpen,
    });

    const tableBindings: FeedbacksTableControllerTableBindings = {
        columns,
        data: reviews,
        rowKey: 'id',
        tableId: 'reviews-table',
        tableScope: 'reviews',
        currentSort: sort,
        pickedData: pickedReviews,
        headerSelectionContent: createElement(TableHeaderSelectAllButton, {
            selectedItemsCount: pickedReviews.length,
            onAllPicked: onAllPickedReviews,
            disabled: isAllPickLoading,
            isSyncing: isReviewsDataReconciliationSelectionVisualRunning,
            showSyncBubbleWhenEmpty: isAllPickLoading,
        }),
        showHeaderCheckbox: false,
        headerSecondaryRow,
        onRowClick,
        onHeaderCheckboxChange,
        onCheckboxChange,
        isHeaderChecked,
        isSelectionSyncing: isReviewsDataReconciliationSelectionVisualRunning,
        checkboxData,
    };

    return buildFeedbacksTableControllerBindings({
        table: tableBindings,
        modals: modalsBindings,
    });
};

export type FeedbacksTableController = ReturnType<typeof useFeedbacksTableController>;
